/// <amd-module name="VSS/Features/PlatformUI/ContributedMenu" />
import * as React from "react";
import { IReadonlyObservableArray, IReadonlyObservableValue, ObservableArray, ObservableCollection } from "VSS/Core/Observable";
import { ILegacyPlatformService } from "VSS/Legacy/Legacy";
import { IVssPageContext } from "VSS/Platform/Context";
import { IContributedActionService, IContributedCommand, IContributedService, IVssContributionService } from "VSS/Platform/Contribution";
import { WrappedComponent } from "VSS/Platform/Layout";
import { traceWarning } from "VSS/Platform/Trace";
import { IIconProps } from "VSSUI/Icon";
import { IMenuItem, IMenuItemDetails, IMenuProps, MenuCell, MenuItemType } from "VSSUI/Menu";
import { contributedIconToIconProps } from "./ContributedIcon";
import { getContributedItems } from "./ContributedItemUtil";

interface IMenuItemContributionData {
    [menuId: string]: IContributedMenuItem[];
}

/**
 * An IContributedMenuItem represents a MenuItem that comes from a contribution.
 *
 * The menu contains 5 columns (all of which are optional):
 *  State - Checkmark or Checkbox
 *  Prefix - Icon or Image
 *  Text - Primary string for the menu item
 *  Secondary Text - Secondary string (Commonly a shortcut)
 *  Action - Intended to be an icon (Commonly submenu chevron)
 */
export interface IContributedMenuItem {
    /**
     * css className that will be added to the root element of the menuItem.
     */
    className?: string;

    /**
     * When set to either true or false the menuItem will show a checkmark in the
     * state column. If it is not readonly the checkmark will be a checkbox.
     */
    checked?: boolean;

    /**
     * This is the serviceName and commandName that will be executed when the menu
     * item is activated.
     */
    command?: IContributedCommand;

    /**
     * The componentType can be specified to render a custom UI for the menu item.
     * This componentType will be used with a WrappedComponent to render.
     */
    componentType?: string;

    /**
     * Caller specified optional data.
     */
    data?: any;

    /**
     * disabled menu items can't have their state changed or be activated.
     */
    disabled?: boolean;

    /**
     * Which group the menu item is associated with. Groups are used in the ordering
     * of the menu.
     */
    groupKey?: string;

    /**
     * When an href is supplied the menu item is rendered as a link.
     */
    href?: string;

    /**
     * Menu items may display and icon or image in the second column.
     */
    iconProps?: IIconProps;

    /**
     * Unique id that is associated with the menu item. The id is added to the root
     * element of the menu item.
     */
    id: string;

    /**
     * Menus support Normal, Header, and Divider items. If the value is not specified
     * the menu item will be a normal menu item.
     */
    itemType?: MenuItemType;

    /**
     * React key that will be used for this menu item.
     */
    key?: string;

    /**
     * The contributed menu this menu item belongs too.
     */
    menuId?: string;

    /**
     * Deprecated: Old style text property. Use text instead.
     */
    name?: string;

    /**
     * rank is used in determining the order of the menu items. Lower ranks come
     * before higher ranks.
     */
    rank?: number;

    /**
     * rel is used when the menu item is a link. This defaults to "noopener".
     */
    rel?: string;

    /**
     * This menu item acts as a submenu. The subMenuId defines the set of menu items
     * that are available on the submenu.
     */
    subMenuId?: string;

    /**
     * target is used when the menu item is a link.
     */
    target?: string;

    /**
     * The primary text of the menu item.
     */
    text?: string;
}

/**
 * Options to use when fetching contributed menu items
 */
export interface IContributedMenuOptions<TContextualData> {
    /**
     * Optional method to customize how menu items are retrieved from an external (iframed) service.
     */
    getExternalMenuItems?: (
        menuService: IExternalMenuItemService<TContextualData>,
        menuId: string,
        contextualData?: TContextualData
    ) => Promise<IExternalMenuItem[]>;

    /**
     * Optional delegate invoked to be able to process each menu item as it is added to
     * the observable collection. If undefined is returned, the menu item is excluded from
     * the menu.
     */
    processExternalMenuItem?: (menuItem: IMenuItem, contribution: IMenuItem | IExternalMenuItem) => IMenuItem | undefined;
}

/**
 * Third-party contributed menu item
 */
export interface IExternalMenuItem {
    action?: (actionContext: any) => void;
    childItems?: IExternalMenuItem[] | Promise<IExternalMenuItem[]>;
    disabled?: boolean;
    hidden?: boolean;
    href?: string;
    icon?: string;
    iconName?: string;
    id?: string;
    groupId?: string;
    menuId?: string;
    rank?: number;
    registeredObjectId?: string;
    separator?: boolean;
    text?: string;
    title?: string;
    uri: string;
}

export interface IExternalMenuItemService<TContextualData = {}> {
    getMenuItems: (menuId: string, contextualData?: TContextualData) => Promise<IExternalMenuItem[]>;
}

function getExternalMenuErrorHandler(pageContext: IVssPageContext, menuId: string): (e: Error) => void {
    return (e: Error) => {
        const message = `Failed to load menu items for ${menuId}: ${JSON.stringify(e || "")}`;
        traceWarning(pageContext, "extensions", "ContributedMenu", message, { menuId });
        console.warn(message);
    };
}

/**
 * To supply menuItems to a contributed menu, a service MUST implement the IMenuService.
 */
export interface IMenuService<TContextualData = {}> {
    /**
     * getMenuItems is called on each registered menu service to gather the available
     * menu items for a given contributed menu.
     *
     * @param menuId The contributionId of the menu being requested.
     * @param contextualData Optional context from the UI hosting the menu
     * @returns If the service has no menu items for this menu, return null.
     *  If the service has a fixed list of menu items return an array.
     *  If the service has a dynamic set of menu items return an ObservableArray of menu items.
     */
    getMenuItems: (menuId: string, contextualData?: TContextualData) => IMenuItem[] | IReadonlyObservableArray<IMenuItem> | null;
}

interface IMenuItemContributionDescriptor {
    id: string;
    contribution: IExternalMenuItem | IContributedMenuItem;
}

function createMenuItemFromDescriptor<TContextualData>(
    pageContext: IVssPageContext,
    descriptor: IMenuItemContributionDescriptor,
    contextualData: TContextualData | undefined,
    trustedSource: boolean,
    options?: IContributedMenuOptions<TContextualData>,
    actionCallback?: () => void
): IMenuItem | undefined {
    let convertedMenuItem: IMenuItem | undefined;
    if ((descriptor.contribution as IExternalMenuItem).uri || !trustedSource) {
        convertedMenuItem = new ExternalContributedMenuItem(pageContext, descriptor.id, descriptor.contribution as IExternalMenuItem, contextualData);
        if (options && options.processExternalMenuItem) {
            convertedMenuItem = options.processExternalMenuItem(convertedMenuItem, descriptor.contribution);
        }

        // If an external menu item was supplied here with no text or icon or submenu, return undefined so that it is excluded.
        // This prevents us from showing an empty menu item.
        if (convertedMenuItem && !convertedMenuItem.text && !convertedMenuItem.iconProps && !convertedMenuItem.subMenuProps) {
            actionCallback?.();
            console.warn("Ignored invalid external menu item: " + descriptor.id);
            return undefined;
        }
    } else {
        convertedMenuItem = new ContributedMenuItem(pageContext, descriptor.contribution as IContributedMenuItem, contextualData);
    }

    return convertedMenuItem;
}

/**
 * Gets an observable array of menu items for the specified menu id. These include
 * contributed menu items that target the menuId as well as those with a menuId property
 * that equals the supplied menuId.
 *
 * @param pageContext Page context
 * @param menuId Contribution id of the menu
 * @param contextualData Optional contextual menu data that is passed to menu providers and action handlers
 * @param options options to specify how contributed items are fetched and processed.
 * @param checkSdkVersion set to true to alter the getMenuItems call based on the sdk version for external services.
 */
export function getMenuItems<TContextualData = {}>(
    pageContext: IVssPageContext,
    menuId: string,
    contextualData?: TContextualData,
    options?: IContributedMenuOptions<TContextualData>,
    checkSdkVersion?: boolean
): IReadonlyObservableArray<IMenuItem> {
    const collection = new ObservableCollection<IMenuItem>();
    addMenuItems(pageContext, collection, menuId, contextualData, options, checkSdkVersion);
    return collection;
}

/**
 * Gets an observable array of menu items for the specified menu id. These include
 * contributed menu items that target the menuId as well as those with a menuId property
 * that equals the supplied menuId.
 *
 * @param pageContext Page context
 * @param menuId Contribution id of the menu
 * @param contextualData Optional contextual menu data that is passed to menu providers and action handlers
 * @param options options to specify how contributed items are fetched and processed.
 * @param checkSdkVersion set to true to alter the getMenuItems call based on the sdk version for external services.
 */
export async function addMenuItems<TContextualData = {}>(
    pageContext: IVssPageContext,
    collection: ObservableCollection<IMenuItem>,
    menuId: string,
    contextualData?: TContextualData,
    options?: IContributedMenuOptions<TContextualData>,
    checkSdkVersion?: boolean
): Promise<void> {
    // Use the contribution service to build the set of available menu-items.
    const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");

    // Determine whether or not there are any menu-items contributed through shared data.
    const menuData: IMenuItemContributionData | undefined = contributionService.getSharedData<IMenuItemContributionData>("_menuItems");
    if (menuData) {
        // If menu items exist for the targeted menu, then update the menu.
        const sharedMenuItems = menuData[menuId];
        if (sharedMenuItems && sharedMenuItems.length > 0) {
            collection.push(sharedMenuItems, c =>
                createMenuItemFromDescriptor(pageContext, { id: c.id, contribution: c }, contextualData, true, options)
            );
        }
    }
    const actionContributions = new Array<{ id: string; contribution: IExternalMenuItem | IContributedMenuItem }>();
    // Add static menu item contributions
    collection.push(getContributedItems<IExternalMenuItem | IContributedMenuItem>(pageContext, menuId, "menuId", "ms.vss-web.menu-item"), c =>
        createMenuItemFromDescriptor(pageContext, c, contextualData, contributionService.isContributionTrusted(c.id), options)
    );
    collection.push(getContributedItems<IExternalMenuItem | IContributedMenuItem>(pageContext, menuId, "menuId", "ms.vss-web.action"), c =>
        createMenuItemFromDescriptor(pageContext, c, contextualData, contributionService.isContributionTrusted(c.id), options, () => {
            actionContributions.push(c);
        })
    );

    // Add dynamic menu item contributions from menu/action providers
    const dynamicContributions = [
        ...getContributedItems(pageContext, menuId, "menuId", "ms.vss-web.menu-provider"),
        ...getContributedItems(pageContext, menuId, "menuId", "ms.vss-web.action-provider"),
        ...actionContributions
    ];
    for (const menuProviderContribution of dynamicContributions) {
        const menuService = contributionService.getServiceSync<IMenuService>(menuProviderContribution.id, true);
        if (menuService) {
            if (menuService.getMenuItems) {
                const localMenuItems = menuService.getMenuItems(menuId, contextualData);
                if (localMenuItems) {
                    collection.push(localMenuItems);
                }
            }
        } else {
            const externalMenuContribution = menuProviderContribution.contribution as IExternalMenuItem;
            if (externalMenuContribution.uri) {
                const contributedService: IContributedService = {
                    serviceName: externalMenuContribution.registeredObjectId || menuProviderContribution.id,
                    uri: externalMenuContribution.uri
                };
                contributionService
                    .getServiceEx<IExternalMenuItemService>(menuProviderContribution.id, contributedService, true)
                    .then(externalMenuService => {
                        if (externalMenuService) {
                            let menuItemsPromise: Promise<IExternalMenuItem[] | null> | undefined;
                            if (options && options.getExternalMenuItems) {
                                menuItemsPromise = options.getExternalMenuItems(externalMenuService, menuId, contextualData);
                            } else if (externalMenuService.getMenuItems) {
                                if (!checkSdkVersion || (externalMenuService as any)._sdkVersion >= 3) {
                                    menuItemsPromise = externalMenuService.getMenuItems(menuId, contextualData);
                                } else {
                                    // Older SDK versions implemented a getMenuItems with no menuId argument.
                                    menuItemsPromise = (externalMenuService as any).getMenuItems(contextualData);
                                }
                            }

                            if (menuItemsPromise) {
                                menuItemsPromise.then(extMenuItems => {
                                    collection.push(extMenuItems ?? [], c => {
                                        const externalItem = new ExternalContributedMenuItem(
                                            pageContext,
                                            menuProviderContribution.id,
                                            { uri: externalMenuContribution.uri, ...(c as Partial<IExternalMenuItem>) },
                                            contextualData
                                        );
                                        if (options && options.processExternalMenuItem) {
                                            return options.processExternalMenuItem(externalItem, c);
                                        }
                                        return externalItem;
                                    });
                                }, getExternalMenuErrorHandler(pageContext, menuProviderContribution.id));
                            }
                        }
                    }, getExternalMenuErrorHandler(pageContext, menuProviderContribution.id));
            } else if (isLWPMenuContribution(contributionService, menuProviderContribution)) {
                // load the legacy SDK Shim module and all required OWP typescript files
                const legacyPlatformService = pageContext.getService<ILegacyPlatformService>("ILegacyPlatformService");
                const modules = await legacyPlatformService.requireModules([
                    "VSS/SDK/Shim",
                    ...menuProviderContribution.contribution.content.require
                ]);

                // best effort to extract the registered menu items from the SDK Shim object registry
                if (typeof modules[0]?.VSS?.getRegisteredObject === "function") {
                    const registeredMenuItems = modules[0].VSS.getRegisteredObject(menuProviderContribution.id);
                    if (typeof registeredMenuItems?.getMenuItems === "function") {
                        const observableMenuItems = new ObservableArray<IMenuItem>([]);
                        const transformLWPMenuItem = (item: any): IMenuItem | undefined => {
                            const externalItem = new ExternalContributedMenuItem(pageContext, menuProviderContribution.id, item, contextualData);
                            if (options && options.processExternalMenuItem) {
                                return options.processExternalMenuItem(externalItem, item);
                            }

                            return externalItem;
                        };

                        const getExternalMenuItems = (menuItems: any) =>
                            Array.isArray(menuItems) ? (menuItems.map(transformLWPMenuItem).filter(item => !!item) as IMenuItem[]) : [];

                        // LWP baked-in menu contributions expect "updateMenuItems" function
                        const context = {
                            ...contextualData,
                            updateMenuItems: (menuItems: any) => {
                                observableMenuItems.value = getExternalMenuItems(menuItems);
                            }
                        };

                        const externalMenuItems = await Promise.resolve(registeredMenuItems.getMenuItems(context));
                        observableMenuItems.value = getExternalMenuItems(externalMenuItems);
                        collection.push(observableMenuItems);
                    }
                }
            } else {
                contributionService.getService<IMenuService>(menuProviderContribution.id).then(menuService => {
                    if (menuService && menuService.getMenuItems) {
                        const localMenuItems = menuService.getMenuItems(menuId, contextualData);
                        if (localMenuItems) {
                            collection.push(localMenuItems);
                        }
                    }
                }, getExternalMenuErrorHandler(pageContext, menuProviderContribution.id));
            }
        }
    }
}

interface LWPMenuItemContribution {
    content: { require: string[] };
    groupId: string;
}

/**
 * Determine if the provided contribution is an internal legacy web platform contribution
 */
function isLWPMenuContribution(
    contributionService: IVssContributionService,
    menuContribution: {
        id: string;
        contribution: unknown;
    }
): menuContribution is {
    id: string;
    contribution: LWPMenuItemContribution;
} {
    if (!contributionService.isContributionTrusted(menuContribution.id)) {
        return false;
    }

    const contributionData = menuContribution.contribution as LWPMenuItemContribution;
    return !!contributionData?.content?.require?.[0] && typeof contributionData.content.require[0] === "string";
}

class ContributedMenuItem<TContextualData> implements IMenuItem {
    private pageContext: IVssPageContext;

    //
    // IMenuItem
    //
    public className?: string;
    public checked?: IReadonlyObservableValue<boolean> | boolean;
    public data?: any;
    public contextualData?: TContextualData;
    public disabled?: boolean;
    public groupKey?: string;
    public href?: string;
    public iconProps?: IIconProps;
    public id: string;
    public itemType?: MenuItemType;
    public onActivate?: (menuItem: IMenuItem, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => boolean | void;
    public rank?: number;
    public readonly?: boolean;
    public rel?: string;
    public renderMenuCell?: (menuCell: MenuCell, menuItem: IMenuItem, details: IMenuItemDetails) => JSX.Element | undefined;
    public renderMenuItem?: (index: number, menuItem: IMenuItem, details: IMenuItemDetails) => JSX.Element;
    public secondaryText?: string;
    public subMenuProps?: IMenuProps;
    public target?: string;
    public text?: string;

    //
    // IContributedMenuItem
    //
    public command?: IContributedCommand;
    public componentType?: string;

    constructor(pageContext: IVssPageContext, contributedMenuItem: IContributedMenuItem, contextualData?: TContextualData) {
        this.pageContext = pageContext;

        this.className = contributedMenuItem.className;
        this.checked = contributedMenuItem.checked;
        this.data = contributedMenuItem.data;
        this.contextualData = contextualData;
        this.disabled = contributedMenuItem.disabled;
        this.groupKey = contributedMenuItem.groupKey;
        this.href = contributedMenuItem.href;
        this.iconProps = contributedMenuItem.iconProps;
        this.id = contributedMenuItem.id || contributedMenuItem.key || "";
        this.rank = contributedMenuItem.rank;
        this.rel = contributedMenuItem.rel;
        this.target = contributedMenuItem.target;
        this.text = contributedMenuItem.text || contributedMenuItem.name;

        // We also support the "-" name to define a Divider.
        if (contributedMenuItem.name === "-") {
            this.itemType = MenuItemType.Divider;
        } else {
            // @NOTE: The menuItem types are equivalant for both old and new platforms'.
            this.itemType = contributedMenuItem.itemType as any as MenuItemType;
        }

        // Ensure we proxy commands through the activation method.
        this.command = contributedMenuItem.command;
        if (this.command || !this.href) {
            this.onActivate = this.onActivateCommand;
        }

        // If there is a SubMenuId we need to setup the submenu.
        const subMenuId = contributedMenuItem.subMenuId;
        if (subMenuId) {
            this.subMenuProps = {
                id: subMenuId,
                items: getMenuItems(pageContext, subMenuId, contextualData)
            };
        }

        // If the menuItem defines a componentType is is used to render the menuItem.
        this.componentType = contributedMenuItem.componentType;
        if (this.componentType) {
            this.renderMenuItem = this.renderMenuItemCustom;
        }
    }

    public onActivateCommand = (menuItem: IMenuItem) => {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const value = this.contextualData ? { ...this.data, ...this.contextualData } : this.data;
        if (this.command) {
            contributionService.executeCommandEx(menuItem.id, this.command, value);
        } else {
            contributionService.getService<IContributedActionService>(menuItem.id, true).then(contributedActionService => {
                if (contributedActionService && contributedActionService.execute) {
                    contributedActionService.execute(value);
                }
            });
        }
    };

    public renderMenuItemCustom = (index: number, item: IMenuItem, details: IMenuItemDetails): JSX.Element => {
        return React.createElement(WrappedComponent, { key: this.id, wrappedType: this.componentType!, wrappedProps: { index, item, details } });
    };
}

let externalMenuItemIdCounter = 0;

class ExternalContributedMenuItem<TContextualData> implements IMenuItem {
    private pageContext: IVssPageContext;
    private externalContributedService?: IContributedService;
    private contextualData?: TContextualData;
    private contributionId: string;

    //
    // IMenuItem
    //
    public disabled?: boolean;
    public groupKey?: string;
    public hidden?: boolean;
    public href?: string;
    public iconProps?: IIconProps;
    public id: string;
    public onActivate?: (menuItem: IMenuItem, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => boolean | void;
    public rank?: number;
    public subMenuProps?: IMenuProps;
    public text?: string;

    constructor(pageContext: IVssPageContext, contributionId: string, contributedMenuItem: IExternalMenuItem, contextualData?: TContextualData) {
        this.pageContext = pageContext;
        this.contributionId = contributionId;
        this.contextualData = contextualData;

        this.disabled = contributedMenuItem.disabled;
        this.groupKey = contributedMenuItem.groupId;
        this.hidden = contributedMenuItem.hidden;
        this.href = contributedMenuItem.href;
        this.id = contributedMenuItem.id || "ext-menu-" + ++externalMenuItemIdCounter;
        this.rank = contributedMenuItem.rank;
        this.text = contributedMenuItem.text || contributedMenuItem.title;

        if (contributedMenuItem.icon) {
            this.iconProps = contributedIconToIconProps(pageContext, contributionId, contributedMenuItem.icon);
        }

        if (!this.iconProps && contributedMenuItem.iconName) {
            this.iconProps = { iconName: contributedMenuItem.iconName };
        }

        if (contributedMenuItem.action) {
            this.onActivate = () => {
                contributedMenuItem.action!(this.contextualData);
            };
        } else if (contributedMenuItem.uri) {
            this.externalContributedService = {
                serviceName: contributedMenuItem.registeredObjectId || contributionId,
                uri: contributedMenuItem.uri,
                configuration: this.contextualData
            };
            this.onActivate = this.onActivateExternalCommand;
        }

        // If there is a MenuId we need to setup the submenu.
        if (contributedMenuItem.childItems) {
            const childItemsArray = new ObservableArray<IMenuItem>();
            if (typeof (contributedMenuItem.childItems as Promise<IExternalMenuItem[]>).then === "function") {
                (contributedMenuItem.childItems as Promise<IExternalMenuItem[]>).then(items => {
                    childItemsArray.push(
                        ...items.map(
                            m =>
                                new ExternalContributedMenuItem(
                                    pageContext,
                                    contributionId,
                                    { uri: contributedMenuItem.uri, ...(m as Partial<IExternalMenuItem>) },
                                    this.contextualData
                                )
                        )
                    );
                }, getExternalMenuErrorHandler(pageContext, this.contributionId));
            } else {
                childItemsArray.push(
                    ...(contributedMenuItem.childItems as IExternalMenuItem[]).map(
                        m =>
                            new ExternalContributedMenuItem(
                                pageContext,
                                contributionId,
                                { uri: contributedMenuItem.uri, ...(m as Partial<IExternalMenuItem>) },
                                this.contextualData
                            )
                    )
                );
            }
            this.subMenuProps = {
                id: "ext-sub-menu-" + ++externalMenuItemIdCounter,
                items: childItemsArray
            };
        }
    }

    public onActivateExternalCommand = () => {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        contributionService
            .getServiceEx<IContributedActionService>(this.contributionId, this.externalContributedService!, true)
            .then(contributedActionService => {
                if (contributedActionService && contributedActionService.execute) {
                    contributedActionService.execute(this.contextualData);
                }
            }, getExternalMenuErrorHandler(this.pageContext, this.contributionId));
    };
}
